import { User } from '../stores/userStore';
import { API_BASE_URL_WITH_VERSION } from '../config/api'

export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
  isExpanded?: boolean;
  metadata?: any;
}

export interface ChatHistoryResponse {
  success: boolean;
  messages: ChatMessage[];
  timestamp?: string;
}

export interface SaveMessageResponse {
  success: boolean;
  message: string;
  timestamp: string;
  conversationId?: string; // Optional field for new conversations
}

export class ChatPersistenceService {
  private baseURL = `${API_BASE_URL_WITH_VERSION}/chat`;
  private user: User | null = null;
  private isInitialized = false;

  // Get JWT token from localStorage
  private getAuthToken(): string | null {
    // First try to get JWT token, fallback to authToken (uid) for development
    const jwtToken = localStorage.getItem('jwtToken');
    if (jwtToken) {
      return `Bearer ${jwtToken}`;
    }
    
    // For development mode, use the uid as a simple identifier
    const uid = localStorage.getItem('authToken');
    if (uid) {
      return `Bearer dev_${uid}`;
    }
    
    return null;
  }

  // Save a single message to the database
  async saveMessage(messageType: 'user' | 'assistant', content: string, metadata: any = {}): Promise<SaveMessageResponse> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      const requestBody = {
        role: messageType,
        content,
        metadata
      };
      
      console.log('🔍 Save message request (no conversation):', {
        url: `${this.baseURL}/save-message`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader ? 'Bearer [HIDDEN]' : 'None'
        },
        body: requestBody
      });
      
      const response = await fetch(`${this.baseURL}/save-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed - please log in again');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error saving chat message:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to save message',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Save a message with conversation ID to the database
  async saveMessageWithConversationId(
    messageType: 'user' | 'assistant', 
    content: string, 
    conversationId: string, 
    metadata: any = {}
  ): Promise<SaveMessageResponse> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      const requestBody = {
        role: messageType,
        content,
        conversationId,
        metadata
      };
      
      console.log('🔍 Save message request:', {
        url: `${this.baseURL}/save-message`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader ? 'Bearer [HIDDEN]' : 'None'
        },
        body: requestBody
      });
      
      const response = await fetch(`${this.baseURL}/save-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed - please log in again');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error saving chat message with conversation ID:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to save message',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Load chat history from the database
  async loadChatHistory(limit: number = 50, conversationId?: string): Promise<ChatMessage[]> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      // If no conversationId is provided, we can't load history from the backend
      // The backend requires a conversationId for the /history endpoint
      if (!conversationId) {
        console.log('🔍 No conversationId provided, skipping history load');
        return [];
      }

      let url = `${this.baseURL}/history?limit=${limit}&conversationId=${conversationId}`;

      console.log('🔍 Loading chat history from:', url, 'with conversationId:', conversationId);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': authHeader
        }
      });

      console.log('🔍 Chat history response status:', response.status, response.statusText);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed - please log in again');
        }
        if (response.status === 404) {
          console.warn('⚠️ Conversation not found, returning empty history');
          return [];
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let result;
      try {
        result = await response.json();
        console.log('🔍 Chat history response from backend:', result);
      } catch (parseError) {
        console.error('❌ Failed to parse response as JSON:', parseError);
        console.log('🔍 Raw response text:', await response.text());
        return [];
      }
      
      if (result.success) {
        if (!result.messages) {
          console.error('❌ Backend response missing messages field:', result);
          return [];
        }
        
        return result.messages.map(msg => ({
          id: msg.id || `db_${Date.now()}_${Math.random()}`,
          type: msg.type,
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          metadata: msg.metadata
        }));
      }
      
      console.warn('⚠️ Backend response not successful:', result);
      return [];
    } catch (error) {
      console.error('Error loading chat history:', error);
      return [];
    }
  }

  // Test endpoint for debugging
  async testSaveEndpoint(): Promise<any> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      const testBody = {
        test: 'data',
        timestamp: new Date().toISOString()
      };
      
      console.log('🧪 Testing save endpoint:', {
        url: `${this.baseURL}/test-save`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader ? 'Bearer [HIDDEN]' : 'None'
        },
        body: testBody
      });
      
      const response = await fetch(`${this.baseURL}/test-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(testBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Test endpoint response:', result);
      return result;
    } catch (error) {
      console.error('❌ Test endpoint error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to test endpoint',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Get recent conversations
  async getRecentConversations(limit: number = 10): Promise<any[]> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      const url = `${this.baseURL}/conversations?limit=${limit}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': authHeader
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed - please log in again');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.conversations && result.conversations.success) {
        // Handle nested structure: result.conversations.conversations
        return result.conversations.conversations || [];
      } else if (result.success && result.conversations) {
        // Handle direct structure: result.conversations
        return Array.isArray(result.conversations) ? result.conversations : [];
      }
      
      return [];
    } catch (error) {
      console.error('Error loading conversations:', error);
      return [];
    }
  }

  // Create a new conversation
  async createConversation(title: string, userId: string): Promise<any> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${this.baseURL}/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          title,
          userId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create conversation',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Update conversation title
  async updateConversationTitle(conversationId: string, title: string): Promise<any> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${this.baseURL}/conversations/${conversationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          title
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating conversation title:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update conversation title',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Save conversation to localStorage as backup
  saveToLocalStorage(messages: ChatMessage[], userId: string): void {
    try {
      const key = `chat_history_${userId}`;
      const data = {
        messages,
        lastUpdated: new Date().toISOString(),
        userId
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  // Load conversation from localStorage as backup
  loadFromLocalStorage(userId: string): ChatMessage[] {
    try {
      const key = `chat_history_${userId}`;
      const data = localStorage.getItem(key);
      
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return [];
    }
  }

  // Clear localStorage chat history
  clearLocalStorage(userId: string): void {
    try {
      const key = `chat_history_${userId}`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  // Delete a conversation
  async deleteConversation(conversationId: string): Promise<{ success: boolean; message: string }> {
    try {
      const authHeader = this.getAuthToken();
      if (!authHeader) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${this.baseURL}/conversation/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed - please log in again');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        success: result.success,
        message: result.message || 'Conversation deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to delete conversation'
      };
    }
  }
}

export const chatPersistenceService = new ChatPersistenceService();
